import { Flower2, Leaf, ShieldCheck, Sun } from "lucide-react";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { Reveal } from "@/components/motion/Reveal";

const PILLARS = [
  { icon: ShieldCheck, title: "Battericida", text: "Un'igiene profonda che non rinuncia alla delicatezza." },
  { icon: Sun, title: "Fungicida", text: "Una protezione in più per la pelle più fragile." },
  { icon: Flower2, title: "Antinfiammatorio", text: "Per una pelle lenita, calma e confortevole." },
  { icon: Leaf, title: "Antiallergico", text: "Formule pensate per le pelli più sensibili." },
];

const ATOMS = [[20, 98], [60, 58], [100, 98], [140, 58], [180, 98], [220, 58], [260, 98], [300, 58], [340, 98]];

const Molecule = () => (
  <svg viewBox="0 0 420 120" className="w-full max-w-md" role="img" aria-label="Formula di struttura del 2-nonenale" fill="none" data-testid="nonenale-molecule">
    <path d="M20 98 L60 58 L100 98 L140 58 L180 98 L220 58 L260 98 L300 58 L340 98" stroke="#E6C3A3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M269 99 L301 67" stroke="#E6C3A3" strokeWidth="3" strokeLinecap="round" />
    <path d="M340 98 L370 68 M347 105 L377 75" stroke="#E8B53A" strokeWidth="3" strokeLinecap="round" />
    {ATOMS.map(([x, y]) => (
      <circle key={`${x}-${y}`} cx={x} cy={y} r="4.5" fill="#F6F0E6" />
    ))}
    <text x="388" y="66" fill="#E8B53A" fontFamily="Jost, sans-serif" fontSize="28" fontWeight="500">O</text>
  </svg>
);

const Formula = () => (
  <div className="shrink-0 text-ivory" data-testid="nonenale-formula">
    <p className="text-[13px] uppercase tracking-[0.28em] text-copper-light">2-nonenale</p>
    <p className="mt-1 font-sans text-3xl font-medium tracking-wide">
      C<sub className="text-lg">9</sub>H<sub className="text-lg">16</sub>O
    </p>
  </div>
);

const PillarCard = ({ icon: Icon, title, text, delay }) => (
  <Reveal delay={delay} className="group flex min-h-[240px] flex-col justify-between rounded-[1.75rem] border border-copper/20 bg-ivory p-8 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1 hover:border-copper/50 hover:shadow-[0_24px_50px_-30px_rgba(15,30,51,0.35)] lg:col-span-3">
    <span className="grid h-14 w-14 place-items-center rounded-full border border-copper/40 text-copper transition-transform duration-700 group-hover:rotate-[20deg]">
      <Icon className="h-6 w-6" strokeWidth={1.4} />
    </span>
    <div>
      <h3 className="mt-8 font-display text-3xl text-navy">{title}</h3>
      <p className="mt-2 text-[16px] leading-relaxed text-slate2">{text}</p>
    </div>
  </Reveal>
);

export const Bento = () => (
  <section id="elicriso" className="bg-cream py-24 lg:py-36" data-testid="benefits-section">
    <div className="container-pf">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow">Le proprietà della linea</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["Sei azioni,", <em className="italic text-copper">una sola delicatezza.</em>]} />
          </h2>
        </div>
        <p className="text-lg leading-relaxed text-slate2 lg:col-span-4 lg:col-start-9">Ogni formula Perfero è pensata per le esigenze specifiche della pelle matura: più sottile, più secca, più sensibile.</p>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">
        <Reveal className="relative flex min-h-[460px] flex-col justify-between overflow-hidden rounded-[2rem] bg-navy p-8 text-ivory sm:col-span-2 sm:p-12 lg:col-span-7" data-testid="bento-nonenale">
          <div>
            <p className="text-[13px] uppercase tracking-[0.28em] text-copper-light">La scienza della pelle matura</p>
            <h3 className="mt-5 font-display text-4xl leading-tight sm:text-5xl">Attivo contro il 2-nonenale</h3>
            <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-ivory/85">Dopo i 40 anni la pelle inizia a produrre 2-nonenale, la molecola legata all'odore tipico della pelle matura. Le formule Perfero lo contrastano, per una freschezza autentica, ogni giorno.</p>
          </div>
          <div className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <Molecule />
            <Formula />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="group relative min-h-[460px] overflow-hidden rounded-[2rem] sm:col-span-2 lg:col-span-5" data-testid="bento-elicriso">
          <img src="/images/elicriso.jpg" alt="Fiori di elicriso dorati" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-8 text-ivory sm:p-10">
            <p className="text-[13px] uppercase tracking-[0.28em] text-copper-light">Il cuore della formula</p>
            <h3 className="mt-4 font-display text-4xl leading-tight">Con postbiotici dell'elicriso</h3>
            <p className="mt-3 text-[17px] text-ivory/90">Il fiore dorato del Mediterraneo, al centro di ogni gesto di cura.</p>
          </div>
        </Reveal>
        {PILLARS.map((p, i) => (
          <PillarCard key={p.title} {...p} delay={i * 0.08} />
        ))}
      </div>
    </div>
  </section>
);
